/**
 * Shared blocky humanoid builder — used for both the player avatar
 * (`render/scene.ts`) and the NPC dialog viewer (`ui/npc-viewer.ts`).
 *
 * Art direction: larger head / smaller body, face always shadowed or masked,
 * top of head never bare. Primitives only, no external assets.
 */
import * as THREE from 'three';
import { tint } from './color-utils';

export interface Humanoid {
  /** Root group — position/rotate this to place the character. */
  group: THREE.Object3D;
  /** Head sub-group, its own pivot — use for idle look/turn animation. */
  head: THREE.Object3D;
  /** All meshes owned by this humanoid, for teardown. */
  meshes: THREE.Mesh[];
  dispose(): void;
}

/**
 * Build a stylised humanoid: legs, torso, arms, and an oversized head with a
 * hood (hides the crown) and a wrapped mask (hides the face). The base body
 * colour drives the whole palette via tinted variants.
 */
export function buildHumanoid(bodyColor: number): Humanoid {
  const meshes: THREE.Mesh[] = [];

  const clothColor = tint(bodyColor, 0.85);
  const wrapColor = tint(bodyColor, 0.35);
  const skinShadow = 0x1c140d;

  const addMesh = (geo: THREE.BufferGeometry, color: number, roughness = 0.85): THREE.Mesh => {
    const mat = new THREE.MeshStandardMaterial({ color, roughness, metalness: 0.05 });
    const mesh = new THREE.Mesh(geo, mat);
    meshes.push(mesh);
    return mesh;
  };

  // ---- proportions (metres) ----
  const legH = 0.6;
  const legW = 0.22;
  const legD = 0.22;
  const legGap = 0.14;
  const torsoH = 0.5;
  const torsoW = 0.58;
  const torsoD = 0.34;
  const armW = 0.15;
  const armD = 0.15;
  const armH = 0.5;
  const headR = 0.38;

  const group = new THREE.Group();

  // legs
  const legY = legH / 2;
  const legL = addMesh(new THREE.BoxGeometry(legW, legH, legD), clothColor);
  legL.position.set(-legGap / 2 - legW / 2, legY, 0);
  const legR = addMesh(new THREE.BoxGeometry(legW, legH, legD), clothColor);
  legR.position.set(legGap / 2 + legW / 2, legY, 0);

  // torso
  const torsoY = legH + torsoH / 2;
  const torso = addMesh(new THREE.BoxGeometry(torsoW, torsoH, torsoD), bodyColor);
  torso.position.set(0, torsoY, 0);

  // arms
  const armX = torsoW / 2 + armW / 2;
  const armL = addMesh(new THREE.BoxGeometry(armW, armH, armD), clothColor);
  armL.position.set(-armX, torsoY, 0);
  const armR = addMesh(new THREE.BoxGeometry(armW, armH, armD), clothColor);
  armR.position.set(armX, torsoY, 0);

  // ---- head (own pivot for idle look/turn) ----
  const head = new THREE.Group();
  const headY = legH + torsoH + headR * 0.55;
  head.position.set(0, headY, 0);

  const skull = addMesh(new THREE.SphereGeometry(headR, 20, 16), skinShadow, 0.9);
  head.add(skull);

  // hood — covers the crown, "top of head never seen"
  const hood = addMesh(
    new THREE.SphereGeometry(headR * 1.05, 20, 16, 0, Math.PI * 2, 0, Math.PI * 0.62),
    wrapColor,
    0.95,
  );
  head.add(hood);

  // wrapped mask — covers the lower face, "face never clearly visible"
  const mask = addMesh(new THREE.BoxGeometry(headR * 1.3, headR * 0.85, headR * 0.6), wrapColor, 0.9);
  mask.position.set(0, -headR * 0.18, headR * 0.62);
  head.add(mask);

  // faint eye glow peeking above the mask line — stylised, not a visible face
  const eyeGeo = new THREE.BoxGeometry(headR * 0.16, headR * 0.06, headR * 0.06);
  const eyeMat = new THREE.MeshStandardMaterial({
    color: 0x2a1c10,
    emissive: 0xffb35c,
    emissiveIntensity: 0.4,
    roughness: 0.5,
  });
  const eyeL = new THREE.Mesh(eyeGeo, eyeMat);
  eyeL.position.set(-headR * 0.22, headR * 0.06, headR * 0.92);
  const eyeR = new THREE.Mesh(eyeGeo, eyeMat);
  eyeR.position.set(headR * 0.22, headR * 0.06, headR * 0.92);
  head.add(eyeL, eyeR);
  meshes.push(eyeL, eyeR);

  group.add(legL, legR, torso, armL, armR, head);

  const dispose = (): void => {
    for (const mesh of meshes) {
      mesh.geometry.dispose();
      const mat = mesh.material;
      if (Array.isArray(mat)) mat.forEach((m) => m.dispose());
      else mat.dispose();
    }
  };

  return { group, head, meshes, dispose };
}
